import ApplicationController from 'javascript/controllers/application_controller';
import { trackGa4Event } from 'javascript/utils/utils';
import * as TicketCartStorage from 'javascript/utils/ticket_cart_storage';

export default class extends ApplicationController {
  static targets = ['display', 'minus', 'plus'];

  get minimum() {
    return parseInt(this.data.get('minimum'), 10);
  }

  get maximum() {
    return parseInt(this.data.get('maximum'), 10);
  }

  get step() {
    return parseInt(this.data.get('step'), 10);
  }

  get cost() {
    return parseFloat(this.data.get('cost'));
  }

  get fees() {
    return parseFloat(this.data.get('fees'));
  }

  get maxOrderLimitReached() {
    return this.data.get('maxOrderLimitReached') === 'true';
  }

  get ticketId() {
    return JSON.parse(this.element.getAttribute('ecommerce-data')).items[0]
      .item_id;
  }

  get eventSlug() {
    const selectionRoot = this.element.closest(
      '[data-controller~="ticket-selection"]',
    );
    return (
      selectionRoot?.dataset?.ticketSelectionEventSlug ||
      window.persistedEventSlug ||
      window.eventSlug ||
      this.eventSlugFromURL()
    );
  }

  eventSlugFromURL() {
    const pathSegments = window.location.pathname.split('/');
    if (pathSegments.length >= 3 && pathSegments[1] === 'events') {
      return pathSegments[2];
    }
    return null;
  }

  get savedState() {
    return TicketCartStorage.getTicket(this.eventSlug, this.ticketId);
  }

  get quantity() {
    if (this._quantity != null) return this._quantity;
    return this.savedState?.ticketSelectorQuantity || 0;
  }

  set quantity(value) {
    this._quantity = value;
    this.displayTarget.textContent = value;
    this.persistQuantity();
  }

  connect() {
    this.autoSelectTicketId = new URLSearchParams(window.location.search)?.get(
      'select_ticket',
    );
    if (this.autoSelectTicketId === this.ticketId) this.autoSelectTicket();

    if (this.savedState) {
      this._quantity = this.savedState.ticketSelectorQuantity;
      this.displayTarget.textContent = this._quantity;
    }

    this.syncSelectedState();

    this.observer = new MutationObserver((mutationList) => {
      if (
        mutationList.some(
          (x) =>
            x.attributeName === 'data-ticket-selector-max-order-limit-reached',
        )
      ) {
        this.enableOrDisablePlusButton();
      }
    });
    this.observer.observe(this.element, { attributes: true });
  }

  disconnect() {
    this.observer.disconnect();
  }

  autoSelectTicket = () => {
    this._quantity = 1;
    this.displayTarget.textContent = '1';
    this.persistQuantity();
    requestAnimationFrame(() => {
      this.dispatchChange();
    });
  };

  increment() {
    let newQuantity = this.quantity + this.step;
    if (newQuantity > this.maximum || this.maxOrderLimitReached) return;
    if (newQuantity < this.minimum) newQuantity = this.minimum;

    this.quantity = newQuantity;
    this.trackEvent('add_to_cart');
  }

  decrement() {
    let newQuantity = this.quantity - this.step;
    if (newQuantity < this.minimum) newQuantity = 0;

    this.quantity = newQuantity;
    this.trackEvent('remove_from_cart');
  }

  trackEvent(action) {
    const ecommerceData = JSON.parse(
      this.element.getAttribute('ecommerce-data'),
    );
    trackGa4Event(action, ecommerceData);
  }

  syncSelectedState() {
    if (this.quantity > 0) {
      this.element.classList.add('is-selected');
    } else {
      this.element.classList.remove('is-selected');
    }

    this.enableOrDisablePlusButton();
  }

  persistQuantity() {
    if (this.quantity > 0) {
      this.element.classList.add('is-selected');
      TicketCartStorage.setTicket(
        this.eventSlug,
        this.ticketId,
        this.ticketStateForQuantity(this.quantity),
      );
    } else {
      this.element.classList.remove('is-selected');
      // Only clear storage when this ticket was previously saved (user decremented).
      if (TicketCartStorage.getTicket(this.eventSlug, this.ticketId)) {
        TicketCartStorage.setTicket(this.eventSlug, this.ticketId, null);
      }
    }

    this.dispatchChange();
    this.enableOrDisablePlusButton();
  }

  dispatchChange() {
    this.element.dispatchEvent(
      new CustomEvent('howler:ticket-selector.change', { bubbles: true }),
    );
  }

  enableOrDisablePlusButton() {
    this.plusTarget.disabled =
      this.quantity >= this.maximum || this.maxOrderLimitReached;
  }

  ticketStateForQuantity(quantity) {
    const totalTicketCost = this.cost * quantity;
    const totalFees = this.fees * quantity;
    const totalCost = totalTicketCost + totalFees;
    const ecommerceData = JSON.parse(
      this.element.getAttribute('ecommerce-data'),
    );

    return {
      ticketSelectorQuantity: quantity,
      ticketSelectorTotalTicketCost: totalTicketCost,
      ticketSelectorTotalFees: totalFees,
      ticketSelectorTotalCost: totalCost,
      ticketSelectorEcommerceData: ecommerceData,
    };
  }
}
